import {
  Badge,
  Box,
  HStack,
  Icon,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Calendar, RefreshCw } from 'lucide-react';
import { FC } from 'react';

interface TimestampData {
  createdAt?: string;
  updatedAt?: string;
  isUpToDate: boolean;
}

interface TimestampMetricsProps {
  timestampData: TimestampData;
}

export const TimestampMetrics: FC<TimestampMetricsProps> = ({
  timestampData,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    return date.toLocaleString();
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      return 'Less than an hour ago';
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Text color="compliance.debug.text" fontSize="sm">
        Validation execution timestamps and data freshness indicators
      </Text>

      {/* Status Overview */}

      {/* Timestamp Details */}
      <VStack align="stretch" spacing={3}>
        {timestampData.createdAt && (
          <Box bg="compliance.debug.code.bg" borderRadius="md" p={3}>
            <Stat size="sm">
              <StatLabel color="compliance.debug.text">
                <HStack>
                  <Icon as={Calendar} size={14} />
                  <Text fontSize="xs">Created</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="compliance.debug.text" fontSize="sm">
                {formatDate(timestampData.createdAt)}
              </StatNumber>
              <StatHelpText color="compliance.debug.text">
                {getRelativeTime(timestampData.createdAt)}
              </StatHelpText>
            </Stat>
          </Box>
        )}

        {timestampData.updatedAt && (
          <Box bg="compliance.debug.code.bg" borderRadius="md" p={3}>
            <Stat size="sm">
              <StatLabel color="compliance.debug.text">
                <HStack>
                  <Icon as={RefreshCw} size={14} />
                  <Text fontSize="xs">Last Updated</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="compliance.debug.text" fontSize="sm">
                {formatDate(timestampData.updatedAt)}
              </StatNumber>
              <StatHelpText color="compliance.debug.text">
                {getRelativeTime(timestampData.updatedAt)}
              </StatHelpText>
            </Stat>
          </Box>
        )}
      </VStack>

      {/* Freshness Indicator */}
      <Box bg="compliance.debug.code.bg" borderRadius="md" p={4}>
        <Text
          color="compliance.debug.text"
          fontSize="sm"
          fontWeight="semibold"
          mb={2}
        >
          Freshness Assessment
        </Text>

        {!timestampData.updatedAt ? (
          <Box
            bg="orange.50"
            border="1px"
            borderColor="orange.200"
            borderRadius="md"
            p={3}
            textAlign="center"
          >
            <Text color="orange.700" fontSize="sm" fontWeight="semibold" mb={1}>
              This document has not been validated
            </Text>
          </Box>
        ) : (
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <Text color="compliance.debug.text" fontSize="xs">
                Validation Age
              </Text>
              <Text color="compliance.debug.text" fontSize="xs">
                {getRelativeTime(timestampData.updatedAt)}
              </Text>
            </HStack>

            <HStack justify="space-between">
              <Text color="compliance.debug.text" fontSize="xs">
                Refresh Needed
              </Text>
              <Badge
                bg={timestampData.isUpToDate ? 'green.500' : 'orange.500'}
                color="white"
                size="sm"
                variant="solid"
              >
                {timestampData.isUpToDate ? 'No' : 'Recommended'}
              </Badge>
            </HStack>
          </VStack>
        )}
      </Box>
    </VStack>
  );
};
